import { useState, useEffect, type RefObject } from 'react'

interface UseBackgroundContrastOptions {
  containerRef?: RefObject<HTMLElement | null>
}

/**
 * Detects whether the background behind a given element is dark or light.
 * Samples the pixel color from a canvas snapshot of the area behind the element.
 */
export function useBackgroundContrast(
  elementRef: RefObject<HTMLElement | null>,
  options: UseBackgroundContrastOptions = {}
): 'dark' | 'light' {
  const [background, setBackground] = useState<'dark' | 'light'>('light')

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    const check = () => {
      const rect = element.getBoundingClientRect()
      const x = rect.left + rect.width / 2
      const y = rect.top + rect.height / 2

      // Find the element behind at the center point
      // Need to hide the element and its fixed/absolute positioned ancestors
      // because visibility:hidden doesn't remove from hit test
      const elementsToHide: HTMLElement[] = []
      let current: HTMLElement | null = element
      while (current) {
        const position = getComputedStyle(current).position
        if (position === 'fixed' || position === 'absolute' || position === 'sticky') {
          elementsToHide.push(current)
        }
        current = current.parentElement
      }
      // Also include the original element
      if (!elementsToHide.includes(element)) {
        elementsToHide.push(element)
      }

      const originalDisplays = elementsToHide.map(el => el.style.display)
      elementsToHide.forEach(el => el.style.display = 'none')

      const behind = document.elementFromPoint(x, y)

      elementsToHide.forEach((el, i) => el.style.display = originalDisplays[i])

      if (!behind) return

      // Traverse up to find non-transparent background
      let elementToCheck: Element | null = behind
      let bg = 'rgba(0, 0, 0, 0)'

      while (elementToCheck) {
        const style = getComputedStyle(elementToCheck)
        bg = style.backgroundColor

        // Check if background is not transparent
        const alphaMatch = bg.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/)
        if (alphaMatch) {
          const alpha = alphaMatch[4] ? parseFloat(alphaMatch[4]) : 1
          if (alpha > 0.1) {
            break // Found a non-transparent background
          }
        }

        // Check for background-image (gradient or image)
        if (style.backgroundImage && style.backgroundImage !== 'none') {
          const bgImage = style.backgroundImage

          // Handle oklch() colors in gradients
          if (bgImage.includes('oklch')) {
            const oklchMatch = bgImage.match(/oklch\(([\d.]+)\s+([\d.]+)\s+([\d.]+)\)/)
            if (oklchMatch) {
              const lightness = parseFloat(oklchMatch[1])
              // OKLCH lightness: 0 = black, 1 = white
              // If lightness > 0.6, treat as light background
              if (lightness > 0.6) {
                bg = 'rgb(255, 255, 255)' // Light
              } else {
                bg = 'rgb(50, 50, 50)' // Dark
              }
              break
            }
          }

          // Handle rgb() colors in gradients
          if (bgImage.includes('gradient')) {
            const colorMatches = bgImage.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/g)
            if (colorMatches && colorMatches.length > 0) {
              bg = colorMatches[0]
              break
            }
          }

          // For other images/gradients, assume light background (common for gradients)
          bg = 'rgb(255, 255, 255)'
          break
        }

        elementToCheck = elementToCheck.parentElement
      }

      // Default to white if no non-transparent background found
      if (!elementToCheck || bg === 'rgba(0, 0, 0, 0)' || bg === 'transparent') {
        bg = 'rgb(255, 255, 255)' // Default to white
      }

      const match = bg.match(/\d+/g)
      if (match && match.length >= 3) {
        const [r, g, b] = match.map(Number)
        // Relative luminance formula
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

        // Check for pink/magenta tones (similar to the button color)
        // Pink has high red, medium-low green, medium-high blue, and red > green
        const isPinkish = r > 180 && r > g + 20 && b > g - 20 && g < 150

        if (isPinkish || luminance < 0.5) {
          setBackground('dark')
        } else {
          setBackground('light')
        }
      }
    }

    check()

    const observer = new IntersectionObserver(check, {
      root: options.containerRef?.current,
      threshold: [0, 0.5, 1],
    })
    observer.observe(element)

    window.addEventListener('scroll', check, { passive: true })
    window.addEventListener('resize', check, { passive: true })

    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', check)
      window.removeEventListener('resize', check)
    }
  }, [elementRef, options.containerRef])

  return background
}
